import { t } from '../i18n';
import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { formatMinor, parseAmountToMinor, type BudgetDto, type CurrencyCode } from '@trestly/shared';
import {
  useCategories,
  useCreateCategory,
  useUpdateBudget,
  useUpdateCategory,
  useUpsertBudget,
} from '../api/hooks';
import { Field, FieldGroup } from './Field';
import { Sheet } from './Sheet';
import { Spinner } from './Spinner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ENVELOPE_PALETTE, envelopeColor, paletteAt } from '../lib/envelope';
import { errorText } from '../lib/errors';
import { money } from '../lib/money';
import { periodLabel } from '../lib/period';
import { confirmAction, haptic } from '@/telegram';
import { cn } from '@/lib/utils';

interface EnvelopeSheetProps {
  open: boolean;
  /** null — новый конверт. */
  budget: BudgetDto | null;
  period: string;
  baseCurrency: CurrencyCode;
  onClose: () => void;
  /** Конверт убран в архив — экран конверта должен закрыться. */
  onArchived?: () => void;
}

/** Лимит: пусто — 0 (конверт просто учитывает траты), иначе неотрицательная сумма. */
function parseLimit(value: string, currency: CurrencyCode): number | undefined {
  const trimmed = value.trim().replace(',', '.');
  if (trimmed === '') return 0;
  try {
    const minor = parseAmountToMinor(trimmed, currency);
    return minor >= 0 ? minor : undefined;
  } catch {
    return undefined;
  }
}

/**
 * Конверт целиком в одном листе: имя, цвет и лимит на период. Цвет — из
 * фирменной палитры: свой цвет по пипетке сделал бы соседние конверты
 * неразличимыми.
 */
export function EnvelopeSheet({
  open,
  budget,
  period,
  baseCurrency,
  onClose,
  onArchived,
}: EnvelopeSheetProps): JSX.Element {
  // С архивными: позиция нового конверта не должна совпасть с убранным.
  const categories = useCategories(true);
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const updateBudget = useUpdateBudget(period);
  const upsertBudget = useUpsertBudget(period);

  const nextPosition =
    (categories.data ?? []).reduce((max, category) => Math.max(max, category.position), -1) + 1;
  const currentColor = budget
    ? envelopeColor(budget.categoryColor, budget.categoryPosition)
    : paletteAt(nextPosition);

  const [name, setName] = useState('');
  const [limit, setLimit] = useState('');
  const [color, setColor] = useState(currentColor);
  const [submitted, setSubmitted] = useState(false);
  /**
   * Конверт уже создан, а лимит не записался (сеть): повтор должен идти сразу
   * в лимит, иначе второй POST упрётся в уникальность имени.
   */
  const createdId = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setName(budget?.categoryName ?? '');
    setLimit(budget && budget.limitMinor > 0 ? formatMinor(budget.limitMinor, baseCurrency) : '');
    setColor(currentColor);
    setSubmitted(false);
    createdId.current = null;
    for (const mutation of [createCategory, updateCategory, updateBudget, upsertBudget]) mutation.reset();
    // мутации меняются каждый рендер — намеренно не в зависимостях
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, budget?.categoryId]);

  const trimmed = name.trim();
  const limitMinor = parseLimit(limit, baseCurrency);
  const nameError = submitted && trimmed === '' ? t('components.nameRequired') : null;
  const limitError = submitted && limitMinor === undefined ? t('components.amountInvalid') : null;
  const busy =
    createCategory.isPending || updateCategory.isPending || updateBudget.isPending || upsertBudget.isPending;
  const error = createCategory.error ?? updateCategory.error ?? updateBudget.error ?? upsertBudget.error;

  const saveLimit = async (categoryId: number, budgetId: number, minor: number): Promise<void> => {
    // id: 0 — строки budgets в этом периоде ещё нет: PATCH /api/budgets/0 дал бы 404.
    if (budgetId === 0) await upsertBudget.mutateAsync({ categoryId, limitMinor: minor });
    else await updateBudget.mutateAsync({ id: budgetId, limitMinor: minor });
  };

  const submit = async (): Promise<void> => {
    setSubmitted(true);
    if (trimmed === '' || limitMinor === undefined) {
      haptic.result('error');
      return;
    }
    try {
      if (budget === null) {
        if (createdId.current === null) {
          const created = await createCategory.mutateAsync({ name: trimmed, position: nextPosition, color });
          createdId.current = created.id;
        }
        if (limitMinor > 0) await saveLimit(createdId.current, 0, limitMinor);
      } else {
        const patch: { name?: string; color?: string } = {};
        if (trimmed !== budget.categoryName) patch.name = trimmed;
        if (color !== currentColor) patch.color = color;
        if (Object.keys(patch).length > 0) await updateCategory.mutateAsync({ id: budget.categoryId, patch });
        if (limitMinor !== budget.limitMinor) await saveLimit(budget.categoryId, budget.id, limitMinor);
      }
      haptic.result('success');
      onClose();
    } catch {
      haptic.result('error');
    }
  };

  const archive = async (): Promise<void> => {
    if (!budget) return;
    const ok = await confirmAction(
      t('components.envelope.removeConfirm', { name: budget.categoryName }),
      { okText: t('components.envelope.removeOk') },
    );
    if (!ok) return;
    updateCategory.mutate(
      { id: budget.categoryId, patch: { archived: true } },
      {
        onSuccess: () => {
          haptic.result('success');
          onClose();
          onArchived?.();
        },
        onError: () => haptic.result('error'),
      },
    );
  };

  return (
    <Sheet open={open} title={budget ? t('components.envelope.title') : t('components.envelope.new')} onClose={onClose}>
      {/* Предпросмотр: так конверт будет выглядеть в сетке. */}
      <div
        className="envelope-tile mb-4 flex min-h-[112px] flex-col justify-between px-4 pb-4"
        style={{ '--c': color } as React.CSSProperties}
        aria-hidden
      >
        <span className="relative flex min-w-0 items-center gap-2 text-[15px] font-semibold">
          <span className="cat-dot" />
          <span className="truncate">{trimmed || t('components.envelope.new')}</span>
        </span>
        <span className="relative">
          {limitMinor ? (
            <span className="num block text-[22px] leading-none font-semibold">{money(limitMinor, baseCurrency)}</span>
          ) : (
            <span className="block text-[13px] text-muted-foreground">{t('components.noLimit')}</span>
          )}
          <span className="meter mt-3">
            <span style={{ width: limitMinor ? '100%' : '0%' }} />
          </span>
        </span>
      </div>

      <Field label={t('components.name')} error={nameError}>
        <Input
          value={name}
          maxLength={200}
          placeholder={t('components.envelope.namePlaceholder')}
          aria-invalid={nameError !== null}
          onChange={(event) => setName(event.target.value)}
        />
      </Field>

      <Field
        label={t('components.envelope.limit', { period: periodLabel(period), currency: baseCurrency })}
        error={limitError}
        hint={
          budget && budget.carriedMinor > 0
            ? t('components.envelope.carriedHint', { sum: money(budget.carriedMinor, baseCurrency) })
            : t('components.envelope.limitHint')
        }
      >
        <Input
          className="num"
          inputMode="decimal"
          value={limit}
          placeholder="0"
          aria-invalid={limitError !== null}
          onChange={(event) => setLimit(event.target.value)}
        />
      </Field>

      <FieldGroup label={t('components.envelope.color')}>
        <div className="grid grid-cols-8 gap-2" role="radiogroup" aria-label={t('components.envelope.colorAria')}>
          {ENVELOPE_PALETTE.map((swatch) => (
            <button
              key={swatch}
              type="button"
              role="radio"
              aria-checked={color === swatch}
              aria-label={swatch}
              className={cn(
                'flex aspect-square w-full items-center justify-center rounded-full text-white transition-transform active:scale-90',
                color === swatch && 'ring-2 ring-foreground ring-offset-2 ring-offset-background',
              )}
              style={{ background: swatch }}
              onClick={() => {
                haptic.select();
                setColor(swatch);
              }}
            >
              {color === swatch ? <Check className="size-4" strokeWidth={3} aria-hidden /> : null}
            </button>
          ))}
        </div>
      </FieldGroup>

      {error ? <div className="mb-3 text-[13px] text-destructive">{errorText(error)}</div> : null}

      <Button className="mt-1 w-full" disabled={busy} onClick={() => void submit()}>
        {busy ? <Spinner small /> : budget ? t('components.save') : t('components.envelope.create')}
      </Button>
      {budget ? (
        <Button variant="destructive" className="mt-2 w-full" disabled={busy} onClick={() => void archive()}>
          {t('components.envelope.remove')}
        </Button>
      ) : null}
    </Sheet>
  );
}
